import { getMainLanguage, SupportedLocale } from "@/i18n";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { Artwork, ImageSize, TabMenuItems } from "./types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function slugify(str: string | undefined): string {
  if (!str) return "untitled";
  return str
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export async function urlToFile(
  url: string,
  filename: string,
  mimeType: string,
): Promise<File> {
  try {
    const response = await fetch(url, {
      headers: { "Cache-Control": "no-cache" },
    });
    const blob = await response.blob();
    return new File([blob], filename, { type: mimeType });
  } catch (error) {
    console.error("Error converting URL to File:", error);
    throw new Error("Failed to convert URL to File");
  }
}

export function readFile(file: File) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result), false);
    reader.readAsDataURL(file);
  });
}

export async function getFileFromUrl(url: string, title?: string) {
  fetch(url as string)
    .then((response) => response.blob())
    .then((blob) => {
      const urlObj = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = urlObj;
      a.download = title ?? "download";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(urlObj);
      a.remove();
    });
}

export function extractHtmlText(s: string) {
  const span = document.createElement("span");
  span.innerHTML = s;
  return span.textContent || span.innerText;
}

// Recursively parse a JSON String.
// If the input is already an object/array, it returns it as is
// If the input is not a string nor an object/array or if the parsing fails, it returns undefined
export function parseJsonOrUndefined<T>(s: unknown): T | undefined {
  if (typeof s === "object" || Array.isArray(s)) return s as T;

  if (typeof s !== "string") return undefined;

  try {
    const firstParse = JSON.parse(s);

    if (typeof firstParse !== "string") return firstParse as T;

    // TECH_DEBT: in the database, some JSON string are encoded with two sets of quotes
    try {
      return JSON.parse(firstParse) as T;
    } catch {
      return firstParse as T;
    }
  } catch {
    return undefined;
  }
}

export const capitalizeFirstLetter = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);

export type WithPartial<T, K extends keyof T> = Omit<T, K> &
  Partial<Pick<T, K>>;

export type Overwrite<A, B> = Omit<A, keyof B> & B;

export const getPaddingToApplyToCenter = (
  index: number,
  sizes: ImageSize[],
  maxWidth: number,
) => {
  const displayHeights = sizes.map(
    (size) => (Math.min(size.width, maxWidth) * size.height) / size.width,
  );
  const maxSize = Math.max(...displayHeights);
  const padding = (maxSize - displayHeights[index]) / 2;
  return padding;
};

export const chunkArray = <T>(array: T[], size: number): T[][] => {
  const result: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
};

export const chunkArrayVertically = <T>(array: T[], size: number): T[][] => {
  const result: T[][] = Array.from({ length: size }, () => []);
  array.forEach((item, index) => {
    result[index % size].push(item);
  });
  return result;
};

export const getImageSize = (url: string): Promise<ImageSize> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = url;

    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };

    img.onerror = (error) => {
      reject(error);
    };
  });
};

export const filterArrayWithPredicate = <
  T,
  K extends keyof T = {
    [Key in keyof T]: T[Key] extends string ? Key : never;
  }[keyof T],
>(
  array: T[],
  predicate: (value: T[K]) => boolean,
  searchedKeys: K[] = Object.keys(array[0] ?? []) as K[],
) => {
  return array.filter((item) => {
    return searchedKeys.some((key) => predicate(item[key]));
  });
};

export const filterArrayWithSearch = <
  T,
  K extends keyof T = {
    [Key in keyof T]: T[Key] extends string ? Key : never;
  }[keyof T],
>(
  array: T[],
  search: string,
  searchedKeys: K[],
) => {
  if (search.length === 0) return array;
  return filterArrayWithPredicate(
    array,
    (value: T[K]) =>
      typeof value === "string" &&
      value.toLowerCase().includes(search.toLowerCase()),
    searchedKeys,
  );
};

const buildArtworkImageName = (
  artwork: Pick<
    Artwork,
    | "oeuvresDescriptionTitre"
    | "oeuvresDescriptionTechnique"
    | "oeuvresDescriptionSupport"
    | "oeuvresDescriptionDateDeCreation"
    | "relatedAuthor"
  >,
  translatedPosition: string | undefined,
  lang: SupportedLocale,
) =>
  [
    artwork.relatedAuthor?.[0]?.artistesAuteursDescriptionPrenom,
    artwork.relatedAuthor?.[0]?.artistesAuteursDescriptionNom,
    artwork.oeuvresDescriptionTitre,
    artwork.oeuvresDescriptionTechnique?.[lang],
    artwork.oeuvresDescriptionSupport?.[lang],
    artwork.oeuvresDescriptionDateDeCreation,
    translatedPosition,
  ]
    .filter(Boolean)
    .filter((s) => s != undefined)
    .map((s) => slugify(s))
    .join("-");

type ImageNamingParams =
  | {
      primaryType: "artwork";
      artwork: Artwork;
      translatedPosition?: string;
    }
  | {
      primaryType: "thumbnail";
      pageNameInDefaultLanguage: string; // English
      title?: string;
      instanceName: string;
    }
  | {
      primaryType: "CMSPages";
      translatedPageName: string;
      instanceName: string;
    }
  | {
      primaryType: "SectionImportedImage";
      sectionTitle: string;
    };

/***
 * Generates a name for an image intended to be used in the public front.
 *
 * Names are not necessarily unique, it's up to the upload routine to check for duplicates.
 *
 * @param params - The parameters for the image name generation.
 * @returns The generated image name.
 */
export const generateImageName = (params: ImageNamingParams) => {
  switch (params.primaryType) {
    case "artwork":
      return buildArtworkImageName(
        params.artwork,
        params.translatedPosition,
        getMainLanguage(),
      );
    case "thumbnail":
      return [
        params.instanceName,
        params.pageNameInDefaultLanguage,
        params.title,
      ]
        .filter(Boolean)
        .filter((s) => s != undefined)
        .map((s) => slugify(s))
        .join("-");
    case "CMSPages":
      return [params.instanceName, params.translatedPageName]
        .map((s) => slugify(s))
        .join("-");
    case "SectionImportedImage":
      return [params.sectionTitle, "imported"].map((s) => slugify(s)).join("-");
  }
};

export function colorGradient(
  fadeFraction: number,
  hexColor1: string,
  hexColor2: string,
  hexColor3?: string,
) {
  const hexToRgb = (
    hex: string,
  ): { red: number; green: number; blue: number } => {
    let cleaned = hex.replace(/^#/, "");

    if (cleaned.length === 3) {
      cleaned = cleaned
        .split("")
        .map((c) => c + c)
        .join("");
    }

    const num = parseInt(cleaned, 16);
    return {
      red: (num >> 16) & 255,
      green: (num >> 8) & 255,
      blue: num & 255,
    };
  };

  let color1 = hexToRgb(hexColor1);
  let color2 = hexToRgb(hexColor2);
  let fade = fadeFraction;

  if (hexColor3) {
    fade = fade * 2;

    if (fade >= 1) {
      fade -= 1;
      color1 = hexToRgb(hexColor2);
      color2 = hexToRgb(hexColor3);
    }
  }

  const diffRed = color2.red - color1.red;
  const diffGreen = color2.green - color1.green;
  const diffBlue = color2.blue - color1.blue;

  const gradient = {
    red: Math.floor(color1.red + diffRed * fade),
    green: Math.floor(color1.green + diffGreen * fade),
    blue: Math.floor(color1.blue + diffBlue * fade),
  };

  const toHex = (value: number) => value.toString(16).padStart(2, "0");

  return `#${toHex(gradient.red)}${toHex(gradient.green)}${toHex(gradient.blue)}`;
}

export const tabMenuKeyToTranslationKey = (key: TabMenuItems) => {
  // Special cases for typos...
  switch (key) {
    case "author":
      return "dashboardTitleArtistes";
    case "audiovisual":
      return "dashboardTitleAudiovisuels";
    case "photography":
      return "dashboardTitlePhotos";
    case "writing":
      return "dashboardTitleWrittings";
  }

  const plural = key.endsWith("s")
    ? key
    : key.endsWith("y")
      ? key.slice(0, -1) + "ies"
      : key + "s";
  return `dashboardTitle${capitalizeFirstLetter(plural)}`;
};

export const tabMenuKeyToPublicationStatusField = (key: TabMenuItems) => {
  const translationMapping = {
    artwork: "oeuvres",
    auction: "ventes",
    audiovisual: "audiovisuel",
    author: "artistesAuteurs",
    bibliography: "bibliographie",
    collector: "collectionneurs",
    document: "documents",
    exhibition: "expositions",
    multipleprint: "estampesMultiples",
    photography: "photos",
    writing: "ecrits",
  };

  return `${translationMapping[key]}PublicationStatus`;
};

export const isInSearch = (search: string, translatedLabel: string) => {
  return search.length > 0
    ? translatedLabel.toLowerCase().includes(search.toLowerCase())
    : true;
};
